import { Form, Formik } from 'formik';
import { useRef, useState } from 'react';
import * as bd from 'react-basic-design';
import * as yup from 'yup';
import { accountApi } from '../../../api/account-api';
import { code } from '../../../components/basic/code';
import { notify } from '../../../components/basic/notify';
import { T, translate } from '../../../components/basic/text';
import { useWaiting } from '../../../components/basic/use-waiting';
import { FormikNumberInput } from '../../../components/formik/formik-number-input';
import { FormikTextField } from '../../../components/formik/formik-text-field';
import { SectionTitle } from '../../../components/ui/section-title';

export const UserChangePhoneNumber = ({ model, setModel }) => {
    const formRef = useRef();
    const waiting = useWaiting();
    const [captchaCounter, setCaptchaCounter] = useState(0);
    const [status, setStatus] = useState({ _id: 'edit' });
    const onSaveClick = () => {
        if (!formRef.current) return false;
        if (!formRef.current.isValid) return false;
        var data = formRef.current.values;

        if (
            code.isIranPhoneNumber(data.phoneNumber) &&
            !code.isValidIranPhoneNumber(data.phoneNumber)
        ) {
            notify.error(<T>invalid-phone-number</T>);
            return;
        }

        if (data.phoneNumber !== model.user.phoneNumber) {
            waiting.start();
            accountApi
                .generateVerificationCodeForUserProfile(data.phoneNumber, data.captcha)
                .then(result => {
                    setStatus({ ...result, phoneNumber: data.phoneNumber, _id: 'verify' });
                })
                .catch(ex => {
                    notify.error(ex);
                    setCaptchaCounter(captchaCounter + 1);
                    formRef.current.setFieldValue('captcha', '');
                })
                .finally(waiting.stop);
        } else {
            notify.warning(<T>nothing-changed!</T>);
            setModel({ ...model, _id: 'view' });
        }
    };
    const savePhoneNumber = values => {
        waiting.start();
        accountApi
            .changeUserPhoneNumber({
                key: status.key,
                code: values.code,
                phoneNumber: status.phoneNumber,
            })
            .then(() => {
                setModel({
                    ...model,
                    user: { ...model.user, phoneNumber: status.phoneNumber },
                    _id: 'view',
                });
                notify.info(translate('changes-are-saved'));
            })
            .catch(notify.error)
            .finally(waiting.stop);
    };
    const initialValues = { phoneNumber: '', captcha: '' };
    const initialValuesVerify = { code: '' };
    return (
        <>
            <div className="container-fluid">
                <SectionTitle title="change-phone-number" />
                <div>
                    {status._id === 'edit' && (
                        <Formik
                            initialValues={initialValues}
                            validationSchema={yup.object({
                                phoneNumber: yup.string().required(' '),
                                captcha: yup.string().required(' '),
                            })}
                            innerRef={formRef}
                            onSubmit={onSaveClick}
                        >
                            <Form>
                                <p className="alert alert-warning">
                                    <T>the-phone-number-must-belong-to-the-current-user</T>
                                </p>
                                <bd.FormRow label={<T>current-phone-number</T>} labelWidth={4}>
                                    {model.user.phoneNumber}
                                </bd.FormRow>
                                <FormikTextField
                                    labelWidth={4}
                                    label="new-phone-number"
                                    name="phoneNumber"
                                    type="tel"
                                    inputClassName="ltr"
                                    handleEnter
                                    maxLength={11}
                                />

                                <bd.Flex
                                    className="pt-4 p-e-4"
                                    align="center"
                                    gap={3}
                                    content="end"
                                >
                                    <bd.Button
                                        variant="contained"
                                        type="submit"
                                        className="min-w-80"
                                    >
                                        <span>{<T>verify</T>}</span>
                                    </bd.Button>

                                    <bd.Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setModel({ ...model, _id: 'view' })}
                                    >
                                        <span>{<T>cancel</T>}</span>
                                    </bd.Button>
                                </bd.Flex>
                            </Form>
                        </Formik>
                    )}

                    {status._id === 'verify' && (
                        <Formik
                            initialValues={initialValuesVerify}
                            validationSchema={yup.object({
                                code: yup
                                    .string()
                                    .required(' ')
                                    .matches(/^[0-9]+$/, 'must-be-only-digits'),
                            })}
                            onSubmit={savePhoneNumber}
                        >
                            {formik => (
                                <Form>
                                    <T className="mt-2" phone={status.phoneNumber}>
                                        a-message-with-verification-code-was-just-sent-to-@phone
                                    </T>

                                    <FormikNumberInput
                                        labelWidth={4}
                                        label="verification-code"
                                        name="code"
                                        type="tel"
                                        maxLength={5}
                                        className="mt-3"
                                        inputClassName="ltr"
                                        autoFocus
                                        handleEnter
                                    />

                                    <bd.Flex
                                        className="pt-4 p-e-4"
                                        align="center"
                                        gap={3}
                                        content="end"
                                    >
                                        <bd.Button
                                            variant="contained"
                                            disabled={!formik.isValid}
                                            type="submit"
                                            className="min-w-80"
                                        >
                                            <span>{<T>save</T>}</span>
                                        </bd.Button>

                                        <bd.Button
                                            type="button"
                                            variant="outline"
                                            onClick={() => setModel({ ...model, _id: 'view' })}
                                        >
                                            <span>{<T>cancel</T>}</span>
                                        </bd.Button>
                                    </bd.Flex>
                                </Form>
                            )}
                        </Formik>
                    )}
                </div>
            </div>
        </>
    );
};
